import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import PublicLayout from '../components/PublicLayout';
import { API } from '../auth';
import { useLanguage } from '../i18n';

/**
 * Public legal page renderer.
 * Loads /api/legal/{slug} and displays localized title + body.
 * Falls back to Korean if the user's language has no translation.
 */
const PATH_TO_SLUG = {
  '/terms': 'terms',
  '/privacy': 'privacy',
  '/refund-policy': 'refund',
};

export default function LegalPageView() {
  const params = useParams();
  const location = useLocation();
  const slug = params.slug || PATH_TO_SLUG[location.pathname] || 'terms';
  const { language } = useLanguage();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/legal/${slug}`)
      .then((r) => setPage(r.data))
      .catch(() => setPage(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const title = page?.[`title_${language}`] || page?.title_ko || slug;
  const body = page?.[`body_${language}`] || page?.body_ko || '';

  return (
    <PublicLayout>
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-20 md:py-28" data-testid={`legal-page-${slug}`}>
        <p className="eyebrow mb-6">{slug.toUpperCase()}</p>
        <h1 className="h-display text-4xl md:text-5xl mb-12">{title}</h1>

        {loading && <p className="text-[#1A1A1A]/40">Loading…</p>}
        {!loading && !page && (
          <div className="bg-[#F6E9E5] border border-[#E8C5C8] rounded-lg p-6">
            <p className="font-semibold text-[#8E2A4E]">Document not found.</p>
            <p className="text-sm text-[#6E4D3A]/70 mt-2">
              Администратор может создать его в разделе "Юр. документы".
            </p>
          </div>
        )}
        {!loading && page && (
          <article
            className="prose prose-sm max-w-none whitespace-pre-line text-[#1A1A1A]/85 leading-relaxed"
            data-testid="legal-body"
          >
            {body}
          </article>
        )}
        {page?.updated_at && (
          <p className="text-xs text-[#1A1A1A]/40 mt-12 pt-6 border-t border-[#1A1A1A]/10">
            Last updated: {new Date(page.updated_at).toLocaleDateString('ko-KR')}
          </p>
        )}
      </section>
    </PublicLayout>
  );
}
